/**
 * The four Health reports' vocabulary — bata-reqs docs/25 §5.4 B2, ADR 9003, `docs/adr/3003`.
 *
 * Pure: the routes, the URL scope (`?country=` · `?by=` · `?department=` · the source the planner chose —
 * `?runId=`, `?week=` or `?priceRunId=` — and `?measure=` for the trend), the query each read is sent, the
 * words of every measure, unit and absence, the colour a target gives, and the queue each pending count opens.
 * The screens and the exports both read from here, so a file and the page say the same thing.
 *
 * READS FROM THE PINNED CLIENT (bata-platform ADR 9053)
 *   `GET /health/availability?country_key=&group_by=&department_family=&run_id=&limit=&cursor=`
 *   `GET /health/coverage?…&run_id=`
 *   `GET /health/sell-through?…&week=`
 *   `GET /health/markdown-exposure?…&price_run_id=` — the figures are the stock forecast to remain at Zona sale
 *   entry, read from the stock day (ADR 9053 amended 2026-11-03); the price run only picks the price steps
 *   `pending` counts, and stays the page's `sourceId`. Its trend's periods are stock days: `sourceId` null.
 *
 * **One market per page** (ruling Q7): the market is `?country=`, the chapter's own market key, chosen with
 * the programme pages' picker (ADR 9056 opens the only visible one). **No run is defaulted here**: with no
 * `?runId=` the platform answers for the market's newest run and names it (`sourceId`), which the page says.
 *
 * **The routes and the queues are spelled here as constants** — one feature may not import another (G11),
 * so the network-health cockpit, the decision queues and the lifecycle pages are named by path.
 */

import {
  getAvailability, getCoverage, getMarkdownExposure, getSellThrough,
  type HealthFigureModel, type HealthGroupBy, type HealthPendingModel, type HealthReportModel, type HealthTrendModel,
} from '@/api/generated';

import { COUNTRY_PARAM } from '../programme';

// ── the reports and their routes ────────────────────────────────────────────

export const HEALTH_REPORTS = ['availability', 'coverage', 'sell-through', 'markdown-exposure'] as const;
export type HealthReportId = (typeof HEALTH_REPORTS)[number];

export const HEALTH_PATH = '/strategic/health';

export function reportPath(report: HealthReportId): string {
  return `${HEALTH_PATH}/${report}`;
}

/** Level 1 — the report's groups: `/strategic/health/<report>/by?by=…`. */
export function groupsPath(report: HealthReportId): string {
  return `${reportPath(report)}/by`;
}

/** The report a path names — `/strategic/health/coverage/by` → `coverage` — or `null`. Pure. */
export function reportOfPath(pathname: string): HealthReportId | null {
  const found = /^\/strategic\/health\/([a-z-]+)(?:\/by)?\/?$/.exec(pathname);
  const slug = found?.[1] ?? '';
  return (HEALTH_REPORTS as readonly string[]).includes(slug) ? (slug as HealthReportId) : null;
}

// ── the URL scope ────────────────────────────────────────────────────────────

export { COUNTRY_PARAM };
export const BY_PARAM = 'by';
export const DEPARTMENT_PARAM = 'department';
/** Availability's and Coverage's distribute run — the in-season chapter's own key, so a link from the cockpit keeps it. */
export const RUN_ID_PARAM = 'runId';
/** Sell-through's landed week, its Monday (`YYYY-MM-DD`). */
export const WEEK_PARAM = 'week';
/** Markdown exposure's price run: the one whose undecided steps `pending` counts (the figures do not depend on it). */
export const PRICE_RUN_PARAM = 'priceRunId';
/** The measure the trend draws; absent, the report's headline. */
export const MEASURE_PARAM = 'measure';

export const GROUP_BYS: readonly HealthGroupBy[] = ['department_family', 'cluster', 'store', 'article'];

export interface HealthScope {
  readonly country: string | null;
  readonly by: HealthGroupBy | null;
  readonly department: string | null;
  readonly runId: string | null;
  readonly week: string | null;
  readonly priceRunId: string | null;
  readonly measure: string | null;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

function read(params: URLSearchParams, key: string): string | null {
  const value = params.get(key)?.trim() ?? '';
  return value === '' ? null : value;
}

/** The scope a search string states. Pure: no default, no guess — a malformed week is no week. */
export function readHealthScope(search: string): HealthScope {
  const params = new URLSearchParams(search);
  const by = read(params, BY_PARAM);
  const week = read(params, WEEK_PARAM);
  return {
    country: read(params, COUNTRY_PARAM),
    by: by !== null && (GROUP_BYS as readonly string[]).includes(by) ? (by as HealthGroupBy) : null,
    department: read(params, DEPARTMENT_PARAM),
    runId: read(params, RUN_ID_PARAM),
    week: week !== null && ISO_DAY.test(week) ? week : null,
    priceRunId: read(params, PRICE_RUN_PARAM),
    measure: read(params, MEASURE_PARAM),
  };
}

/** `search` with each key set, or removed for `null`; every other key kept. Pure. */
export function withScope(search: string, values: Readonly<Record<string, string | null>>): string {
  const params = new URLSearchParams(search);
  for (const [key, value] of Object.entries(values)) {
    if (value === null || value.trim() === '') params.delete(key);
    else params.set(key, value);
  }
  const query = params.toString();
  return query === '' ? '' : `?${query}`;
}

/** Each report's source key in the URL — the run, the week or the price run the planner chose. */
export const SOURCE_PARAM: Readonly<Record<HealthReportId, string>> = {
  availability: RUN_ID_PARAM,
  coverage: RUN_ID_PARAM,
  'sell-through': WEEK_PARAM,
  'markdown-exposure': PRICE_RUN_PARAM,
};

/** The source the scope names for a report, or `null` (the platform's newest). Pure. */
export function scopeSource(report: HealthReportId, scope: HealthScope): string | null {
  if (report === 'sell-through') return scope.week;
  if (report === 'markdown-exposure') return scope.priceRunId;
  return scope.runId;
}

/** The link to a report's page — level 0 or 1 — for a market, a grouping and a source. Pure. */
export function reportHref(report: HealthReportId, options: {
  readonly country: string | null;
  readonly by?: HealthGroupBy | null;
  readonly department?: string | null;
  readonly source?: string | null;
  readonly measure?: string | null;
}): string {
  const params = new URLSearchParams();
  if (options.country !== null) params.set(COUNTRY_PARAM, options.country);
  const level = options.by === undefined || options.by === null ? 0 : 1;
  if (level === 1 && options.by !== undefined && options.by !== null) params.set(BY_PARAM, options.by);
  if (options.department !== undefined && options.department !== null) params.set(DEPARTMENT_PARAM, options.department);
  if (options.source !== undefined && options.source !== null) params.set(SOURCE_PARAM[report], options.source);
  if (options.measure !== undefined && options.measure !== null) params.set(MEASURE_PARAM, options.measure);
  const query = params.toString();
  return `${level === 0 ? reportPath(report) : groupsPath(report)}${query === '' ? '' : `?${query}`}`;
}

// ── the reads ────────────────────────────────────────────────────────────────

/** The groups one page asks for: the template's table cap (ADR 9008). */
export const GROUP_PAGE_SIZE = 50;

export type HealthOperation = typeof getAvailability;

export const REPORT_OPERATION: Readonly<Record<HealthReportId, HealthOperation>> = {
  availability: getAvailability,
  coverage: getCoverage as HealthOperation,
  'sell-through': getSellThrough as HealthOperation,
  'markdown-exposure': getMarkdownExposure as HealthOperation,
};

/** The query of one read. `null` while the market is missing: the page then sends nothing (§3.4 rule 2). */
export function reportQuery(report: HealthReportId, scope: HealthScope, options: {
  readonly by: HealthGroupBy;
  readonly department: string | null;
  readonly cursor?: string | null;
  readonly limit?: number;
}): { readonly query: Readonly<Record<string, string | number>> } | null {
  if (scope.country === null) return null;
  const query: Record<string, string | number> = { country_key: scope.country, group_by: options.by, limit: options.limit ?? GROUP_PAGE_SIZE };
  if (options.department !== null) query['department_family'] = options.department;
  if (options.cursor !== undefined && options.cursor !== null) query['cursor'] = options.cursor;
  const source = scopeSource(report, scope);
  if (source !== null) {
    if (report === 'sell-through') query['week'] = source;
    else if (report === 'markdown-exposure') query['price_run_id'] = source;
    else query['run_id'] = source;
  }
  return { query };
}

// ── words ────────────────────────────────────────────────────────────────────

export interface ReportWords {
  readonly navKey: string;
  readonly titleKey: string;
  /** The measure the trend draws when the URL names none (O4: the week's sell-through drives Sell-through's). */
  readonly headline: string;
}

export const REPORT_WORDS: Readonly<Record<HealthReportId, ReportWords>> = {
  availability: { navKey: 'strategic.nav.healthAvailability', titleKey: 'strategic.health.availability.title', headline: 'cells_served_in_stock' },
  coverage: { navKey: 'strategic.nav.healthCoverage', titleKey: 'strategic.health.coverage.title', headline: 'cells_low_on_cover' },
  'sell-through': { navKey: 'strategic.nav.healthSellThrough', titleKey: 'strategic.health.sellThrough.title', headline: 'sell_through_week' },
  'markdown-exposure': { navKey: 'strategic.nav.healthMarkdown', titleKey: 'strategic.health.markdown.title', headline: 'markdown_exposure_units' },
};

/** Each measure's label and its (i), written out so the catalogue check sees every key. */
export const MEASURE_WORDS: Readonly<Record<string, { readonly labelKey: string; readonly helpKey: string }>> = {
  cells_served_in_stock: { labelKey: 'strategic.health.measure.cellsServedInStock', helpKey: 'strategic.health.help.cellsServedInStock' },
  cells_out_or_broken: { labelKey: 'strategic.health.measure.cellsOutOrBroken', helpKey: 'strategic.health.help.cellsOutOrBroken' },
  broken_size_runs: { labelKey: 'strategic.health.measure.brokenSizeRuns', helpKey: 'strategic.health.help.brokenSizeRuns' },
  full_price_availability: { labelKey: 'strategic.health.measure.fullPriceAvailability', helpKey: 'strategic.health.help.fullPriceAvailability' },
  cells_low_on_cover: { labelKey: 'strategic.health.measure.cellsLowOnCover', helpKey: 'strategic.health.help.cellsLowOnCover' },
  aged_cover_units: { labelKey: 'strategic.health.measure.agedCoverUnits', helpKey: 'strategic.health.help.agedCoverUnits' },
  median_weeks_of_cover: { labelKey: 'strategic.health.measure.medianWeeksOfCover', helpKey: 'strategic.health.help.medianWeeksOfCover' },
  open_cover_alerts: { labelKey: 'strategic.health.measure.openCoverAlerts', helpKey: 'strategic.health.help.openCoverAlerts' },
  sell_through_season_to_date: { labelKey: 'strategic.health.measure.sellThroughSeasonToDate', helpKey: 'strategic.health.help.sellThroughSeasonToDate' },
  full_price_sell_through: { labelKey: 'strategic.health.measure.fullPriceSellThrough', helpKey: 'strategic.health.help.fullPriceSellThrough' },
  full_price_share_of_sales: { labelKey: 'strategic.health.measure.fullPriceShareOfSales', helpKey: 'strategic.health.help.fullPriceShareOfSales' },
  units_sold_week: { labelKey: 'strategic.health.measure.unitsSoldWeek', helpKey: 'strategic.health.help.unitsSoldWeek' },
  sell_through_week: { labelKey: 'strategic.health.measure.sellThroughWeek', helpKey: 'strategic.health.help.sellThroughWeek' },
  markdown_exposure_units: { labelKey: 'strategic.health.measure.markdownExposureUnits', helpKey: 'strategic.health.help.markdownExposureUnits' },
  markdown_exposure_at_cost: { labelKey: 'strategic.health.measure.markdownExposureAtCost', helpKey: 'strategic.health.help.markdownExposureAtCost' },
  zona_sales_stock_units: { labelKey: 'strategic.health.measure.zonaSalesStockUnits', helpKey: 'strategic.health.help.zonaSalesStockUnits' },
  outlet_stock_units: { labelKey: 'strategic.health.measure.outletStockUnits', helpKey: 'strategic.health.help.outletStockUnits' },
  // No longer on the markdown read (ADR 9053 amended 2026-11-03), still on monthly packs frozen before it.
  undecided_price_steps: { labelKey: 'strategic.health.measure.undecidedPriceSteps', helpKey: 'strategic.health.help.undecidedPriceSteps' },
};

export const UNKNOWN_MEASURE = { labelKey: 'strategic.health.measure.unknown', helpKey: 'strategic.health.help.unknown' } as const;

export function measureWords(measure: string): { readonly labelKey: string; readonly helpKey: string } {
  return MEASURE_WORDS[measure] ?? UNKNOWN_MEASURE;
}

/** Why a figure, a period, a group list or a count is absent — the platform's codes, in words. */
export const ABSENT_REASON_KEY: Readonly<Record<string, string>> = {
  no_distribute_run: 'strategic.health.absent.noDistributeRun',
  no_run_in_period: 'strategic.health.absent.noRunInPeriod',
  period_not_yet_summarised: 'strategic.health.absent.periodNotYetSummarised',
  no_assessed_cells: 'strategic.health.absent.noAssessedCells',
  sell_through_not_landed: 'strategic.health.absent.sellThroughNotLanded',
  no_week_in_period: 'strategic.health.absent.noWeekInPeriod',
  zero_base: 'strategic.health.absent.zeroBase',
  no_price_run: 'strategic.health.absent.noPriceRun',
  no_price_run_in_period: 'strategic.health.absent.noPriceRunInPeriod',
  cover_panel_not_landed: 'strategic.health.absent.coverPanelNotLanded',
  no_panel_month: 'strategic.health.absent.noPanelMonth',
  cover_bands_not_in_force: 'strategic.health.absent.coverBandsNotInForce',
  cover_distribution_edges_malformed: 'strategic.health.absent.coverDistributionEdgesMalformed',
  no_cover_measured: 'strategic.health.absent.noCoverMeasured',
  zona_exposure_not_store_grained: 'strategic.health.absent.zonaExposureNotStoreGrained',
  zona_minimum_age_not_governed: 'strategic.health.absent.zonaMinimumAgeNotGoverned',
  stock_not_landed: 'strategic.health.absent.stockNotLanded',
  no_stock_day_in_period: 'strategic.health.absent.noStockDayInPeriod',
  landed_cost_currencies_differ: 'strategic.health.absent.landedCostCurrenciesDiffer',
  cluster_scheme_not_frozen: 'strategic.health.absent.clusterSchemeNotFrozen',
  no_lifecycle_run_in_month: 'strategic.health.absent.noLifecycleRunInMonth',
  no_move_run: 'strategic.health.absent.noMoveRun',
  no_move_run_in_tick: 'strategic.health.absent.noMoveRunInTick',
  move_run_failed: 'strategic.health.absent.moveRunFailed',
  landed_cost_not_in_force: 'strategic.health.absent.landedCostNotInForce',
  phase_unknown: 'strategic.health.absent.phaseUnknown',
  end_of_life_or_outlet_phase: 'strategic.health.absent.endOfLifeOrOutletPhase',
  markdown_in_force_at_majority: 'strategic.health.absent.markdownInForceAtMajority',
};

export const UNKNOWN_ABSENCE_KEY = 'strategic.health.absent.unknown';

/**
 * Every problem code the platform's Health router registers (bata-platform `services/api/routers/health.py`,
 * ADR 9053). The OpenAPI document publishes the problem shape, not the code vocabulary, so this list is the
 * census: the Health tests hold `KNOWN_PROBLEM_CODES` (`src/api/problem.ts`) to it both ways, and each code to a
 * message in all three locales — a code missing here or there reads «a problem this version does not recognise».
 */
export const HEALTH_PROBLEM_CODES = [
  'planning.health.unavailable',
  'planning.health.not_found',
  'planning.health.cluster_needs_department_family',
  'planning.health.lookup_window_incomplete',
  'planning.health.lookup_window_invalid',
  'planning.health.lookup_too_many_pairs',
  'planning.health.lookup_key_empty',
] as const;

/** The words of an absence code; an unknown code keeps its code as `{code}`. Pure. */
export function absentReasonKey(code: string | null): string {
  return code === null ? UNKNOWN_ABSENCE_KEY : ABSENT_REASON_KEY[code] ?? UNKNOWN_ABSENCE_KEY;
}

// ── figures ──────────────────────────────────────────────────────────────────

/** A cluster key the platform gives a store with no membership in force — shown, never dropped (O7). */
export const CLUSTER_UNASSIGNED = 'cluster_unassigned';

/** Units that are counts: a total sums what is known, and is never absent (owner rule 2026-10-14). */
const COUNT_UNITS: ReadonlySet<string> = new Set(['cells', 'units', 'alerts', 'steps', 'amount']);

/**
 * Why a total cannot be summed at all — not «nothing known», which is nought, but no sum to make: no
 * minimum age to place the Zona sale entry, no stock day, or landed costs in two currencies (bata-platform
 * ADR 9053 amended 2026-11-03). Such a total is absent, in words, never 0.
 */
export const UNSUMMABLE_TOTAL_REASONS: ReadonlySet<string> = new Set([
  'zona_minimum_age_not_governed', 'stock_not_landed', 'no_stock_day_in_period', 'landed_cost_currencies_differ',
]);

/**
 * The value a figure is SHOWN with. A share or a median with no base is absent, in words. A total —
 * cells, units, alerts, steps or an amount — is always a number: what is known, nought when nothing is
 * (owner rule 2026-10-14, «totals always a number»), and how much is known goes in its (i) — unless the
 * platform says no sum can be made (`UNSUMMABLE_TOTAL_REASONS`). Pure.
 */
export function shownValue(figure: Pick<HealthFigureModel, 'value' | 'unit'> & { readonly absentReason?: string | null }): number | null {
  if (figure.value !== null) return figure.value;
  if (figure.absentReason !== undefined && figure.absentReason !== null && UNSUMMABLE_TOTAL_REASONS.has(figure.absentReason)) return null;
  return COUNT_UNITS.has(figure.unit) ? 0 : null;
}

/** Whether a figure is a share (drawn ×100 as percentage points on a chart). */
export function isShare(unit: string): boolean {
  return unit === 'share';
}

/** The polarity `DeltaValue` reads; a figure without one has a band, not a side. */
export function polarityOf(polarity: string | null): 'higher-is-better' | 'lower-is-better' | 'none' {
  if (polarity === 'higher_is_better') return 'higher-is-better';
  if (polarity === 'lower_is_better') return 'lower-is-better';
  return 'none';
}

/** The colour a management target gives a figure (bata-platform ADR 9054), as a status and its words. */
export const RAG_STATUS: Readonly<Record<string, { readonly level: 'good' | 'warning' | 'critical'; readonly labelKey: string }>> = {
  green: { level: 'good', labelKey: 'strategic.health.rag.green' },
  amber: { level: 'warning', labelKey: 'strategic.health.rag.amber' },
  red: { level: 'critical', labelKey: 'strategic.health.rag.red' },
};

/** The status a figure's target gives it, or `null` with no target in force (no colour: as before). Pure. */
export function ragOf(figure: Pick<HealthFigureModel, 'target'>): { readonly level: 'good' | 'warning' | 'critical'; readonly labelKey: string } | null {
  const rag = figure.target?.rag ?? null;
  return rag === null ? null : RAG_STATUS[rag] ?? null;
}

/** One figure of a list by measure, or `undefined`. */
export function figureOf(figures: readonly HealthFigureModel[], measure: string): HealthFigureModel | undefined {
  return figures.find((figure) => figure.measure === measure);
}

/**
 * The measures a report's groups carry, in the order of its first row — the table's columns. A
 * full-price flag (`breakdown.fullPrice` on an article row, no value) is a column of its own words. Pure.
 */
export function groupMeasures(rows: readonly { readonly figures: readonly HealthFigureModel[] }[]): readonly string[] {
  const seen: string[] = [];
  for (const row of rows) for (const figure of row.figures) if (!seen.includes(figure.measure)) seen.push(figure.measure);
  return seen;
}

/** Whether an article row's full-price figure is O2's verdict (in or out, with its store counts). */
export function isFullPriceVerdict(figure: HealthFigureModel): boolean {
  return figure.measure === 'full_price_availability' && 'fullPrice' in figure.breakdown && 'rangedStores' in figure.breakdown;
}

// ── the trend ────────────────────────────────────────────────────────────────

/** The trend that carries a measure, and the measures every trend offers, in order. Pure. */
export function trendOf(trends: readonly HealthTrendModel[], measure: string): HealthTrendModel | null {
  return trends.find((trend) => trend.measures.includes(measure)) ?? null;
}

export function trendMeasures(trends: readonly HealthTrendModel[]): readonly string[] {
  return [...new Set(trends.flatMap((trend) => trend.measures))];
}

/** The measure the trend draws: the URL's, when a trend carries it, else the report's headline. Pure. */
export function chosenMeasure(report: HealthReportId, trends: readonly HealthTrendModel[], asked: string | null): string {
  const offered = trendMeasures(trends);
  if (asked !== null && offered.includes(asked)) return asked;
  const headline = REPORT_WORDS[report].headline;
  return offered.includes(headline) || offered.length === 0 ? headline : offered[0] ?? headline;
}

/** Each trend grain's words: what one period is. */
export const GRAIN_KEY: Readonly<Record<string, string>> = {
  cycle_period: 'strategic.health.grain.cyclePeriod',
  fiscal_week: 'strategic.health.grain.fiscalWeek',
  month: 'strategic.health.grain.month',
  price_run: 'strategic.health.grain.priceRun',
};

// ── pending: the queues ──────────────────────────────────────────────────────

/** The queues, as their chapters mount them (G11: spelled, never imported). */
export const QUEUE_PATH = {
  replenishment_proposals: '/inseason/allocation-decisions',
  moves: '/inseason/compacting-moves',
  price_steps: '/inseason/markdown/approvals',
  alerts: '/foundations/exceptions/group',
  lifecycle_transitions: '/exit/lifecycle',
  lifecycle_outlet: '/exit/lifecycle-outlet',
} as const;

export const PENDING_WORDS: Readonly<Record<string, string>> = {
  replenishment_proposals: 'strategic.health.pending.replenishmentProposals',
  moves: 'strategic.health.pending.moves',
  price_steps: 'strategic.health.pending.priceSteps',
  alerts: 'strategic.health.pending.alerts',
  lifecycle_transitions: 'strategic.health.pending.lifecycleTransitions',
  lifecycle_outlet: 'strategic.health.pending.lifecycleOutlet',
};

export const UNKNOWN_PENDING_KEY = 'strategic.health.pending.unknown';

function href(path: string, params: Readonly<Record<string, string>>): string {
  return `${path}?${new URLSearchParams(params).toString()}`;
}

/**
 * The list a pending count opens, with exactly the filter the platform counted it under (ADR 9069), or
 * `null` when the count is not a link: `linkable` false, no count, a source this build does not know, or
 * a filter the queue cannot be given. Pure.
 */
export function pendingHref(item: Pick<HealthPendingModel, 'source' | 'count' | 'linkable' | 'filter'>): string | null {
  if (!item.linkable || item.count === null) return null;
  const filter = item.filter;
  switch (item.source) {
    case 'replenishment_proposals':
      return filter['runId'] === undefined ? null : href(QUEUE_PATH.replenishment_proposals, { runId: filter['runId'] });
    case 'moves':
      return filter['moveRunId'] === undefined ? null : href(QUEUE_PATH.moves, { moveRunId: filter['moveRunId'] });
    case 'price_steps': {
      if (filter['runId'] === undefined) return null;
      const article = filter['article_key'];
      return href(QUEUE_PATH.price_steps, article === undefined ? { runId: filter['runId'] } : { runId: filter['runId'], optionKeys: article });
    }
    case 'alerts': {
      const { rule, country, queue, state } = filter;
      if (rule === undefined || country === undefined) return null;
      // The alert group page, grouped by market on the market: the alerts of one rule in it.
      return href(QUEUE_PATH.alerts, { rule, by: 'country', key: country, queue: queue ?? 'all', state: state ?? 'open' });
    }
    case 'lifecycle_transitions':
    case 'lifecycle_outlet': {
      const { market, month } = filter;
      if (market === undefined || month === undefined) return null;
      return href(QUEUE_PATH[item.source], { market, month: month.slice(0, 7) });
    }
    default:
      return null;
  }
}

// ── drilling out of Health ───────────────────────────────────────────────────

/** The network-health cockpit's store and article pages (ch04's routes, `docs/adr/9046`). */
export const NETWORK_HEALTH_PATH = '/inseason/network-health';

/**
 * Where a level-1 row leads beyond Health: Availability's and Coverage's stores and articles open the
 * cockpit's own page for the same run; Markdown exposure's articles open their price steps in the
 * approvals queue; everything else stops here. Pure.
 */
export function rowOutHref(report: HealthReportId, by: HealthGroupBy, key: string, data: Pick<HealthReportModel, 'sourceId' | 'provenance'>): string | null {
  if ((report === 'availability' || report === 'coverage') && data.sourceId !== null) {
    if (by === 'store') return href(`${NETWORK_HEALTH_PATH}/store/${encodeURIComponent(key)}`, { runId: data.sourceId });
    if (by === 'article') return href(`${NETWORK_HEALTH_PATH}/article/${encodeURIComponent(key)}`, { runId: data.sourceId });
  }
  if (report === 'markdown-exposure' && by === 'article' && data.sourceId !== null) {
    return href(QUEUE_PATH.price_steps, { runId: data.sourceId, optionKeys: key });
  }
  return null;
}

// ── provenance ───────────────────────────────────────────────────────────────

/** A provenance field as a string, or `null`. */
export function provenanceText(report: Pick<HealthReportModel, 'provenance'>, key: string): string | null {
  const value = report.provenance[key];
  return typeof value === 'string' && value !== '' ? value : typeof value === 'number' ? String(value) : null;
}

/** The families whose cluster level shows (O7: frozen schemes only). Empty until one is frozen. Pure. */
export function clusterFamilies(report: Pick<HealthReportModel, 'provenance'> | undefined): readonly string[] {
  const value = report?.provenance['clusterFamilies'];
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
}

/** The groupings a level-1 page offers inside a family: the cluster level only where its scheme is frozen. Pure. */
export function groupingsOffered(department: string | null, families: readonly string[]): readonly HealthGroupBy[] {
  if (department === null) return ['department_family', 'store', 'article'];
  return families.includes(department) ? ['cluster', 'store', 'article'] : ['store', 'article'];
}
